import React, { useCallback, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useAdminLanguage } from "../../../context/AdminLanguageContext";
import PuzzleBoardInventoryPanel from "../../pzl_common/stats/PuzzleBoardInventoryPanel";
import PuzzlePlayUserStatsPanel from "../../pzl_common/stats/PuzzlePlayUserStatsPanel";
import StatsJigsawService from "../../../services/StatsJigsawService";
import JigsawPromotionPanel from "./JigsawPromotionPanel";
import JigsawTagStatsPanel from "./JigsawTagStatsPanel";
import JigsawTopUsersPanel from "./JigsawTopUsersPanel";
import JigsawTopPuzzlesPanel from "./JigsawTopPuzzlesPanel";
import JigsawBasketStatsPanel from "./JigsawBasketStatsPanel";

const TAB_USERS = 0;
const TAB_TOP_USERS = 1;
const TAB_TOP_PUZZLES = 2;
const TAB_PUZZLES = 3;
const TAB_TAGS = 4;
const TAB_PROMOTION = 5;
const TAB_BASKET = 6;
const VALID_TABS = [
    TAB_USERS,
    TAB_TOP_USERS,
    TAB_TOP_PUZZLES,
    TAB_PUZZLES,
    TAB_TAGS,
    TAB_PROMOTION,
    TAB_BASKET,
];

const RANGE_STORAGE_KEY = "admin.jigsaw.userStats.range";
const EXCLUDE_TEST_STORAGE_KEY = "admin.jigsaw.userStats.excludeTest";
const EXCLUDE_STAFF_STORAGE_KEY = "admin.jigsaw.userStats.excludeStaff";
const TAB_STORAGE_KEY = "admin.jigsaw.stats.tab";

function loadStoredTab() {
    try {
        const v = parseInt(window.localStorage.getItem(TAB_STORAGE_KEY), 10);
        if (VALID_TABS.includes(v)) return v;
    } catch {}
    return TAB_USERS;
}

export default function AdminJigsawStatsMain() {
    const { language } = useAdminLanguage();
    const [activeTab, setActiveTab] = useState(loadStoredTab);
    // A panel loads its stats on mount, so mount it on first visit and keep it mounted after that.
    const [visitedTabs, setVisitedTabs] = useState(() => [loadStoredTab()]);

    const handleTabChange = (event, value) => {
        setActiveTab(value);
        setVisitedTabs((tabs) => (tabs.includes(value) ? tabs : [...tabs, value]));
        try {
            window.localStorage.setItem(TAB_STORAGE_KEY, String(value));
        } catch {}
    };

    const fetchUserPlays = useCallback((params) => StatsJigsawService.userPlayStats(params), []);
    const fetchByDifficulty = useCallback((lang) => StatsJigsawService.statsByDifficulty(lang), []);

    const tabPanelSx = (tab) => ({
        display: activeTab === tab ? "flex" : "none",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "auto",
    });

    return (
        <Box
            sx={{
                p: 2,
                minHeight: "100%",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Typography variant="h5" sx={{ fontWeight: 600, color: "primary.main", mb: 1, flexShrink: 0 }}>
                Jigsaw Statistics
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2, flexShrink: 0 }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="Jigsaw statistics sections">
                    <Tab
                        value={TAB_USERS}
                        label="Users"
                        id="jigsaw-stats-tab-0"
                        aria-controls="jigsaw-stats-tabpanel-0"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        value={TAB_TOP_USERS}
                        label="Top users"
                        id="jigsaw-stats-tab-1"
                        aria-controls="jigsaw-stats-tabpanel-1"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        value={TAB_TOP_PUZZLES}
                        label="Top puzzles"
                        id="jigsaw-stats-tab-2"
                        aria-controls="jigsaw-stats-tabpanel-2"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        value={TAB_PUZZLES}
                        label="Puzzles"
                        id="jigsaw-stats-tab-3"
                        aria-controls="jigsaw-stats-tabpanel-3"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        value={TAB_BASKET}
                        label="Basket"
                        id="jigsaw-stats-tab-6"
                        aria-controls="jigsaw-stats-tabpanel-6"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        value={TAB_TAGS}
                        label="Tags"
                        id="jigsaw-stats-tab-4"
                        aria-controls="jigsaw-stats-tabpanel-4"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                    <Tab
                        value={TAB_PROMOTION}
                        label="Promotion"
                        id="jigsaw-stats-tab-5"
                        aria-controls="jigsaw-stats-tabpanel-5"
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
                    />
                </Tabs>
            </Box>

            {/* Panels stay mounted while hidden so switching tabs does not reload their stats */}
            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_USERS}
                id="jigsaw-stats-tabpanel-0"
                aria-labelledby="jigsaw-stats-tab-0"
                sx={tabPanelSx(TAB_USERS)}
            >
                {visitedTabs.includes(TAB_USERS) && (
                    <PuzzlePlayUserStatsPanel
                        language={language}
                        fetchStats={fetchUserPlays}
                        rangeStorageKey={RANGE_STORAGE_KEY}
                        excludeTestStorageKey={EXCLUDE_TEST_STORAGE_KEY}
                        excludeStaffStorageKey={EXCLUDE_STAFF_STORAGE_KEY}
                        difficultyChartTitle="Puzzle sizes played"
                        difficultyChartHint="Plays grouped by tile grid and difficulty — 6×6 is 36 pieces, 9×9 is 81, and so on."
                        notificationAppLabel="Jigsaw Muse"
                        csvFilePrefix="jigsaw"
                    />
                )}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_TOP_USERS}
                id="jigsaw-stats-tabpanel-1"
                aria-labelledby="jigsaw-stats-tab-1"
                sx={tabPanelSx(TAB_TOP_USERS)}
            >
                {visitedTabs.includes(TAB_TOP_USERS) && <JigsawTopUsersPanel language={language} />}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_TOP_PUZZLES}
                id="jigsaw-stats-tabpanel-2"
                aria-labelledby="jigsaw-stats-tab-2"
                sx={tabPanelSx(TAB_TOP_PUZZLES)}
            >
                {visitedTabs.includes(TAB_TOP_PUZZLES) && <JigsawTopPuzzlesPanel language={language} />}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_PUZZLES}
                id="jigsaw-stats-tabpanel-3"
                aria-labelledby="jigsaw-stats-tab-3"
                sx={tabPanelSx(TAB_PUZZLES)}
            >
                {visitedTabs.includes(TAB_PUZZLES) && (
                    <PuzzleBoardInventoryPanel
                        language={language}
                        fetchStats={fetchByDifficulty}
                        sizeColumnLabel="Tiles / difficulty"
                        showPieces
                    />
                )}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_TAGS}
                id="jigsaw-stats-tabpanel-4"
                aria-labelledby="jigsaw-stats-tab-4"
                sx={tabPanelSx(TAB_TAGS)}
            >
                {visitedTabs.includes(TAB_TAGS) && (
                    <JigsawTagStatsPanel language={language} />
                )}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_PROMOTION}
                id="jigsaw-stats-tabpanel-5"
                aria-labelledby="jigsaw-stats-tab-5"
                sx={tabPanelSx(TAB_PROMOTION)}
            >
                {visitedTabs.includes(TAB_PROMOTION) && <JigsawPromotionPanel language={language} />}
            </Box>

            <Box
                role="tabpanel"
                hidden={activeTab !== TAB_BASKET}
                id="jigsaw-stats-tabpanel-6"
                aria-labelledby="jigsaw-stats-tab-6"
                sx={tabPanelSx(TAB_BASKET)}
            >
                {visitedTabs.includes(TAB_BASKET) && <JigsawBasketStatsPanel language={language} />}
            </Box>
        </Box>
    );
}
